import { getZeegUtmParams } from "@/lib/utm";

export const ZEEG_SUCCESS_PATH = "/termin/danke";

const ZEEG_SCRIPT_SRC = "https://assets.zeeg.me/embed.min.js";

const SUCCESS_MARKERS = [
  "booking_completed",
  "booking-completed",
  "event_scheduled",
  "event-scheduled",
  "booking_success",
  "booking-success",
  "scheduled",
];

function looksLikeZeegSuccess(data: unknown): boolean {
  if (data == null) return false;
  if (typeof data === "string") {
    const lower = data.toLowerCase();
    if (SUCCESS_MARKERS.some((m) => lower.includes(m))) return true;
    try {
      return looksLikeZeegSuccess(JSON.parse(data));
    } catch {
      return false;
    }
  }
  if (typeof data !== "object") return false;
  const obj = data as Record<string, unknown>;
  const candidates = [obj.type, obj.event, obj.name, obj.action, obj.status]
    .filter((v): v is string => typeof v === "string")
    .map((v) => v.toLowerCase());
  if (candidates.some((c) => SUCCESS_MARKERS.some((m) => c.includes(m)))) return true;
  // Nested payload
  for (const key of ["payload", "data", "detail"]) {
    if (looksLikeZeegSuccess((obj as Record<string, unknown>)[key])) return true;
  }
  return false;
}

declare global {
  interface Window {
    __zeegSuccessListenerInstalled?: boolean;
  }
}

export function installZeegSuccessListener() {
  if (typeof window === "undefined") return;
  if (window.__zeegSuccessListenerInstalled) return;
  window.__zeegSuccessListenerInstalled = true;

  window.addEventListener("message", (event: MessageEvent) => {
    try {
      const origin = event.origin || "";
      if (!origin) return;
      const host = new URL(origin).hostname;
      if (!/(^|\.)zeeg\.me$/i.test(host)) return;
    } catch {
      return;
    }
    if (!looksLikeZeegSuccess(event.data)) return;
    const target = resolveZeegSuccessPath(lastZeegEvent);
    if (window.location.pathname === target) return;
    window.location.assign(target);
  });
}

/**
 * Ticket TR1: Ziel der Weiterleitung je Zeeg-Terminseite.
 *
 * Die Zeeg-Nachricht nennt die gebuchte Terminseite nicht belegbar. Deshalb
 * zählt die zuletzt von dieser Seite geöffnete Terminseite (Popup oder
 * eingebetteter Kalender). Unbekannt → bisheriges Ziel `/termin/danke`.
 */
export const MENTORING_SUCCESS_PATH = "/mentoring/danke";
let lastZeegEvent: string | null = null;

export function rememberZeegEvent(eventPath: string): void {
  lastZeegEvent = eventPath;
}

export function resolveZeegSuccessPath(eventPath: string | null): string {
  if (!eventPath) return ZEEG_SUCCESS_PATH;
  const slug = eventPath.split("/").filter(Boolean).pop()?.toLowerCase() ?? "";
  if (slug.includes("mentoring")) return MENTORING_SUCCESS_PATH;
  return ZEEG_SUCCESS_PATH;
}
const ZEEG_STYLE_HREF = "https://assets.zeeg.me/embed.min.css";

export type ZeegPopupOptions = {
  url: string;
  backgroundColor?: string;
  textColor?: string;
  primaryColor?: string;
  utm?: Record<string, string | null>;
};

declare global {
  interface Window {
    Zeeg?: { initPopupWidget: (opts: ZeegPopupOptions) => void };
  }
}

export function ensureZeegAssets() {
  if (typeof document === "undefined") return;
  if (!document.querySelector(`link[href="${ZEEG_STYLE_HREF}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = ZEEG_STYLE_HREF;
    document.head.appendChild(link);
  }
  if (!document.querySelector(`script[src="${ZEEG_SCRIPT_SRC}"]`)) {
    const script = document.createElement("script");
    script.src = ZEEG_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }
}

/**
 * Lädt das Zeeg-Skript für ein eingebettetes Widget (Inline-Kalender).
 * Nutzt denselben Skript-/Stil-Lader wie das Popup — keine zweite Umsetzung.
 */
export function ensureZeegInlineWidget(eventType: string) {
  if (typeof document === "undefined") return;
  rememberZeegEvent(eventType);
  ensureZeegAssets();
  const selector = `script[src="${ZEEG_SCRIPT_SRC}"][data-event-type="${eventType}"]`;
  if (document.querySelector(selector)) return;
  const script = document.createElement("script");
  script.src = ZEEG_SCRIPT_SRC;
  script.async = true;
  const attrs: Record<string, string> = {
    "data-user": "creategy",
    "data-event-type": eventType,
    "data-redirect-parent": "true",
    "data-background-color": "FFFDFA",
    "data-text-color": "212121",
    "data-primary-color": "004B51",
    "data-hide-details": "1",
  };
  for (const [key, value] of Object.entries(attrs)) script.setAttribute(key, value);
  document.body.appendChild(script);
}

export async function waitForZeeg(timeoutMs = 3000): Promise<Window["Zeeg"] | null> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (typeof window !== "undefined" && window.Zeeg?.initPopupWidget) return window.Zeeg;
    await new Promise((r) => setTimeout(r, 50));
  }
  return null;
}

export async function openZeegPopup(eventPath: string) {
  rememberZeegEvent(eventPath);
  ensureZeegAssets();
  const zeeg = await waitForZeeg();
  if (!zeeg) return;
  const { source, medium, campaign, content, term, gclid } = getZeegUtmParams();
  const url = new URL(`https://zeeg.me/${eventPath}`);
  if (gclid) url.searchParams.set("gclid", gclid);
  zeeg.initPopupWidget({
    url: url.toString(),
    backgroundColor: "#FFFDFA",
    textColor: "#212121",
    primaryColor: "#004B51",
    utm: { source, medium, campaign, content, term, gclid },
  });
}
